import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, useFetch, StatusPill } from "../../components/crm/ui";
import { Plus, Trash2, Edit3, Mail, Phone, Search, Building2 } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", skill: "", service_team_member_id: "" };

export default function AdminTechnicians() {
  const { data: techs, reload } = useFetch("/technicians");
  const { data: team } = useFetch("/service-team");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const teamMap = useMemo(() => {
    const m = {}; team.forEach((t) => { m[t.id] = t; }); return m;
  }, [team]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return techs;
    return techs.filter((t) =>
      [t.name, t.phone, t.email, t.skill, teamMap[t.service_team_member_id]?.firm_name,
       teamMap[t.service_team_member_id]?.name].join(" ").toLowerCase().includes(q),
    );
  }, [techs, search, teamMap]);

  function openCreate() { setEditing(null); setForm(EMPTY); setModalOpen(true); }
  function openEdit(t) {
    setEditing(t);
    setForm({ ...EMPTY, ...t, service_team_member_id: t.service_team_member_id || "" });
    setModalOpen(true);
  }
  function up(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form, service_team_member_id: form.service_team_member_id || null };
      if (editing) {
        await api.put(`/technicians/${editing.id}`, payload);
        toast.success("Technician updated");
      } else {
        await api.post("/technicians", payload);
        toast.success("Technician created");
      }
      setModalOpen(false); reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  }

  async function remove(t) {
    if (!confirm(`Delete technician "${t.name}"?`)) return;
    try { await api.delete(`/technicians/${t.id}`); toast.success("Deleted"); reload(); }
    catch (err) { toast.error(err.message); }
  }

  function memberLabel(m) {
    if (!m) return "—";
    return m.firm_name || m.name || m.contact_person || m.email;
  }

  return (
    <div className="space-y-6" data-testid="tech-page">
      <PageHeader
        overline="Field Team"
        title="Technicians"
        action={
          <PrimaryButton onClick={openCreate} testid="create-tech-btn">
            <Plus className="w-4 h-4" /> New
          </PrimaryButton>
        }
      />

      <div className="bg-white border border-[#E2E4DC]">
        <div className="px-4 py-3 border-b border-[#E2E4DC] flex items-center gap-3 bg-[#F4F6F1]">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name / phone / skill / service team…"
              data-testid="tech-search"
              className="w-full pl-8 pr-2 py-1.5 text-xs font-mono border border-[#DDE5D8] focus:border-[#1F4E2C] outline-none bg-white"
            />
          </div>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#475569]">
            <span className="text-[#0F1A12] font-semibold">{filtered.length}</span> / {techs.length}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-[0.25em] text-[#4A5568] border-b border-[#E2E4DC]">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Skill</th>
                <th className="px-4 py-3">Service Team</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const m = teamMap[t.service_team_member_id];
                return (
                  <tr key={t.id} className="border-b border-[#E2E4DC] hover:bg-[#F4F5F0]" data-testid={`tech-row-${t.id}`}>
                    <td className="px-4 py-3 font-display font-semibold text-[#0F1A12]">{t.name}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-[#475569]">
                      <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{t.phone}</div>
                      <div className="flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" />{t.email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#0F1A12]">{t.skill || "—"}</td>
                    <td className="px-4 py-3">
                      {m ? (
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-mono px-2 py-1 bg-purple-50 text-purple-900 border border-purple-200" data-testid={`tech-team-${t.id}`}>
                          <Building2 className="w-3 h-3" />
                          {memberLabel(m)}
                          <StatusPill value={m.type === "service_franchisee" ? "Franchisee" : "Manager"} tone={m.type === "service_franchisee" ? "info" : "success"} />
                        </div>
                      ) : (
                        <span className="text-[11px] font-mono text-[#94A3B8]">Unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(t)} data-testid={`tech-edit-${t.id}`} className="p-1.5 hover:text-[#1F4E2C]"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(t)} data-testid={`tech-delete-${t.id}`} className="p-1.5 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-[#4A5568]">
                  {techs.length === 0 ? "No technicians yet." : "No technicians match your search."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit Technician" : "New Technician"}>
        <form onSubmit={(e) => { e.preventDefault(); save(); }} className="space-y-4">
          <Field label="Name *"><TextInput required value={form.name} onChange={(e) => up("name", e.target.value)} data-testid="tech-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone *"><TextInput required value={form.phone} onChange={(e) => up("phone", e.target.value)} data-testid="tech-phone" /></Field>
            <Field label="Email *"><TextInput type="email" required value={form.email} onChange={(e) => up("email", e.target.value)} data-testid="tech-email" /></Field>
          </div>
          <Field label="Skill"><TextInput value={form.skill} onChange={(e) => up("skill", e.target.value)} placeholder="e.g. Solar installer, AMC technician" data-testid="tech-skill" /></Field>
          <Field label="Tag to Service Team Member (optional)">
            <Select value={form.service_team_member_id} onChange={(e) => up("service_team_member_id", e.target.value)} data-testid="tech-team-select">
              <option value="">— Unassigned —</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>
                  {memberLabel(m)} · {m.type === "service_franchisee" ? "Franchisee" : "Manager"}
                </option>
              ))}
            </Select>
          </Field>
          <p className="text-[11px] font-mono text-[#475569] bg-[#FAFBF7] border border-[#DDE5D8] p-2">
            Tagging a technician to a Service Manager makes them visible (read-only) in that Manager's portal.
            Franchisees normally add their own technicians from the Service Portal.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-[#4A5568]">Cancel</button>
            <PrimaryButton type="submit" disabled={saving} testid="tech-save-btn">{saving ? "Saving…" : "Save"}</PrimaryButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
